import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CuentaTrabajo } from 'src/app/dashboard/interfaces/cuenta-trabajo.interface';
import { WorkingAccountService } from 'src/app/dashboard/services/working-account.service';
import {
  CuentaBancariaVes,
  CuentasBancariasVesService,
} from 'src/app/dashboard/services/cuentas-bancarias-ves.service';

interface CuentaTrabajoOption extends CuentaTrabajo {
  yaRegistrada: boolean;
}

@Component({
  selector: 'app-create-cuenta-bancaria-ves-modal',
  templateUrl: './create-cuenta-bancaria-ves-modal.component.html',
  styleUrls: ['./create-cuenta-bancaria-ves-modal.component.sass'],
})
export class CreateCuentaBancariaVesModalComponent implements OnInit {
  form: FormGroup;
  cuentasTrabajo: CuentaTrabajoOption[] = [];
  cuentaSeleccionada: CuentaTrabajoOption | null = null;
  loading = false;
  loadingCuentas = true;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateCuentaBancariaVesModalComponent>,
    private cuentasBancariasVesService: CuentasBancariasVesService,
    private workingAccountService: WorkingAccountService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      cuentaTrabajoId: ['', Validators.required],
      nombreTitular: [{ value: '', disabled: true }],
      rifCedula: [{ value: '', disabled: true }],
      banco: [{ value: '', disabled: true }],
      tipoCuenta: [{ value: '', disabled: true }],
      numeroCuenta: [{ value: '', disabled: true }],
    });
  }

  ngOnInit(): void {
    this.loadCuentasTrabajo();
  }

  async loadCuentasTrabajo(): Promise<void> {
    this.loadingCuentas = true;
    try {
      const [cuentasTrabajo, cuentasRegistradas] = await Promise.all([
        this.workingAccountService.getWorkingAccounts(['VE']),
        this.cuentasBancariasVesService.getAll().toPromise() as Promise<CuentaBancariaVes[]>,
      ]);

      const registradasSet = new Set(
        (cuentasRegistradas || []).map((c) => c.numeroCuenta)
      );

      this.cuentasTrabajo = cuentasTrabajo
        .filter((ct) => ct.activo)
        .map((ct) => ({
          ...ct,
          yaRegistrada: registradasSet.has(ct.numeroCuenta),
        }))
        .sort((a, b) => {
          if (a.yaRegistrada !== b.yaRegistrada) return a.yaRegistrada ? 1 : -1;
          return (a.banco?.nombre || '').localeCompare(b.banco?.nombre || '');
        });
    } catch {
      this.snackBar.open('Error al cargar cuentas de trabajo', 'Ok', {
        duration: 3000,
      });
    } finally {
      this.loadingCuentas = false;
    }
  }

  onCuentaSelected(cuentaTrabajoId: number): void {
    const cuenta = this.cuentasTrabajo.find(
      (c) => c.cuenta_trabajo_id === cuentaTrabajoId
    );
    this.cuentaSeleccionada = cuenta || null;

    if (cuenta) {
      this.form.patchValue({
        nombreTitular: `${cuenta.nombre} ${cuenta.apellidos}`.trim(),
        rifCedula: cuenta.numeroID || '',
        banco: cuenta.banco?.nombre || '',
        tipoCuenta: cuenta.tipoCuenta?.nombre?.toLowerCase() || '',
        numeroCuenta: cuenta.numeroCuenta || '',
      });
    }
  }

  onSubmit(): void {
    if (this.form.get('cuentaTrabajoId')?.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.cuentaSeleccionada?.yaRegistrada) {
      this.snackBar.open('Esta cuenta ya está registrada', 'Ok', {
        duration: 3000,
      });
      return;
    }

    const payload = {
      nombreTitular: this.form.getRawValue().nombreTitular,
      rifCedula: this.form.getRawValue().rifCedula,
      numeroCuenta: this.form.getRawValue().numeroCuenta,
      tipoCuenta: this.form.getRawValue().tipoCuenta,
      banco: this.form.getRawValue().banco,
    };

    this.loading = true;
    this.cuentasBancariasVesService.create(payload).subscribe({
      next: () => {
        this.snackBar.open('Cuenta bancaria registrada correctamente', 'Ok', {
          duration: 3000,
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading = false;
        const msg = typeof err === 'string' ? err : 'Error al crear la cuenta';
        this.snackBar.open(msg, 'Ok', { duration: 4000 });
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
